"use client"
import { type CSSProperties, type ReactNode } from "react"
import { GraphAudioControls } from "@/app/audio/GraphAudioControls"
import { LaborDotStripLeaderRows } from "../labor-mix/dot-strip/LaborDotStripLeaderRows"
import { SafetyTaxIndicator } from "../indicators/SafetyTaxIndicator"
import { AlignmentResearcherCount } from "../indicators/AlignmentResearcherCount"
import { RdSpeedupStat, TakeoverRiskStat } from "../indicators/CapabilityRiskStats"
import { EmploymentRate } from "../indicators/EmploymentRate"
import { MedianIncome } from "../indicators/MedianIncome"
import { AnimateYear } from "../plan-a/AnimateYear"
import { GalaxyHide } from "./blocks/layout/GalaxyHide"
import { HudPersistentGlobeAndGraph } from "./blocks/layout/HudPersistentGlobeAndGraph"
import { PoliciesPanel } from "./blocks/policy/PoliciesPanel"
import { DistributionOfPowerBubbles4Fade } from "./blocks/power/DistributionOfPowerBubbles4Fade"
import {
  GlobeHoverPointerGate,
  GlobeHoverSwap,
  GlobeHoverSwapTarget,
  type GlobeHoverListLayout,
  type GlobeHoverListRowHighlight,
  type GlobeHoverListVariant,
} from "./blocks/globe/globeHoverSwap"
import type { GlobeEarthHoverTreatment } from "../globe/datacenterScreenPositions"
import type {
  PostChoiceViewportSettings,
} from "../extrapolated-graph/types"
import { HUD87_POLICY_STATUS_APPEARANCE } from "./blocks/policy/policies-panel/policyAppearance"
import {
  CapabilityHoverBridge,
  CapabilityHoverLaborDivider,
  CapabilityHoverSwap,
  CapabilityHoverSwapTarget,
  type CapabilityLadderDescriptionLayout,
  type CapabilityLadderHighlight,
  type CombinedHierarchyId,
  type CombinedInfoLayoutId,
  type LadderColumnClarityDesign,
  type CombinedRiskLayout,
  type CombinedRiskLine,
  type CombinedRiskSemantics,
  type RiskTedPanelDesignId,
  type RiskHoverDetailVisual,
  type RiskHoverTedDefinition,
} from "./blocks/capability/capabilityHoverSwap"
import type { RiskAmbientDesignId, RiskFoomDesignId, RiskFoomPreviewMode, RiskSpaceExplorerMode, RiskTedGuideMode } from "../extrapolated-graph/RiskFoomPreviewOverlay"
import {
  METRIC_TRIGGER_RED_CHIP_STYLE,
  MetricHoverSwap,
  MetricHoverSwapTarget,
} from "./blocks/metrics/metricHoverSwap"
import { HoverSwapSequentialFade } from "./blocks/hoverSwapFade"
import { ClaimPowerHoverOnEnter, HoverSwapStickyZone } from "./blocks/hoverSwapStickyZone"
import {
  UnifiedHudPanels,
  type UnifiedCapabilityCardDesign,
} from "./blocks/panels/unifiedHoverPanels"
import { BelowHudDetailSwapTarget } from "./blocks/belowHudDetailSwap"
import {
  HudHoverClickHintDisabledContext,
  HudHoverHeaderTreatmentContext,
  HudHoverRuleTreatmentContext,
  type HudHoverHeaderTreatmentId,
  type HudHoverRuleTreatmentId,
} from "./blocks/hudHoverDetailHeader"
import { type MetricPanelDesignId } from "./metric-details/panel-redesigns/metricPanelRedesigns"
import type { MobileStripDesignId } from "../plan-a/mobile-strip/stripVariants"
import { ClearHudHoversWhileDetailOpen } from "./metric-details/detailAnimations"
import {
  PolicyHoverSwap,
  PolicyHoverSwapTarget,
  type PolicyDetailTreatment,
} from "./blocks/policy/policyHoverSwap"
import { type PolicyMapDesignId } from "./blocks/policy/policyMapHoverDetail"
import { type ChoosePathDesignId } from "../branch-node/choosePathDesigns"
import {
  LaborHoverRowTarget,
  LaborHoverSectionHighlight,
  LaborHoverSwap,
  type LaborHoverDesignId,
} from "./blocks/labor/laborHoverSwap"
import { type PolicySquareHighlight } from "./blocks/policy/policies-panel/policyAppearance"
import {
  PowerHoverClickHintAboveTreemap,
  PowerHoverHideWhileHovered,
  PowerHoverSwap,
  PowerHoverSwapTarget,
  PowerHoverWorldBelowTreemap,
  type PowerBreakdownMetricLabel,
  type PowerBreakdownRegionEmphasis,
  type PowerFrontierSplitDesign,
  type PowerHoverClickHint,
} from "./blocks/power/powerHoverSwap"

// HUD 69 — shared corner radius for BOTH the policy boxes and the
// distribution-of-power cells, so the two grids round identically. Slightly
// rounder than the treemap default (BLOCK_BORDER_RADIUS_PX = 2).
export const HUD69_BOX_RADIUS_PX = 4

// HUD 70 — the distribution-of-power cells round a little less than the policy
// boxes (which stay at HUD69_BOX_RADIUS_PX = 4).
export const HUD70_POWER_RADIUS_PX = 3

// HUD 87 — color of the solid (pre-dashed) capability line segment and the
// filled dot at its solid→dashed transition point. Matches the takeover-risk
// dither's dark red.
export const HUD87_CAPABILITY_PAST_LINE_COLOR = "#8B0000"

// HUD 87 — radius of the filled dot at the solid→dashed transition point
// (default current-point radius is 3).
export const HUD87_CURRENT_POINT_DOT_RADIUS_PX = 4.45

// HUD 87 — stroke width of the solid (pre-dashed) capability line segment
// (matches the default 4).
export const HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX = 4

// HUD 87 — the graph's date never goes below this, even at the very top of
// the article where the page's first data-chart-date marker rewinds the HUD
// to 2025-01. With the timeline starting 2026-01 (4-month data points sit
// ~45.5px apart), Jul 2026 keeps ~68px of the red capability line drawn
// behind the dot instead of letting it collapse to zero length.
export const HUD87_GRAPH_MINIMUM_DATE = new Date("2026-07-01T00:00:00Z")

// HUD 87 — the rendered capability line (and its endpoint dot) never dips
// below this y-axis value. The takeover-risk dither's bottom edge sits at
// the band's lowest min value (1.15);
// flooring the line there keeps its flat left tail riding that edge instead
// of dropping ~20px below it into the strip the year ticks used to occupy.
export const HUD87_CAPABILITY_LINE_FLOOR_VALUE = 1.15

// HUD 87 — per-status policy box treatment. Main defines this HERE (its
// MobileHUD imports it from variants); this branch's hover system keeps the
// single source of truth in policyAppearance.ts (its policy components need
// it without importing variants, which would cycle) — re-exported so main's
// callers keep working.
export { HUD87_POLICY_STATUS_APPEARANCE } from "./blocks/policy/policies-panel/policyAppearance"

// HUD 89 — date-driven post-choice viewport ladder. Today this drives the
// MOBILE strip's frame only (plan-a/mobile-strip/camera/); the desktop graph's
// camera runs on the authored zoomKeyframes.json (see /zoom-editor) instead.
// The y-axis max follows the story year through these anchors (linearly
// interpolated between them, clamped outside), and the timeline shows only
// futureWindowYears past the current story year, sliding forward as the
// reader scrolls. Anchors track Plan A's capability curve (2.2 → 3.8 → 11 →
// 39 → 71 → plateau ~86 → 2041+ foom) so the line rides at roughly half the
// frame height the whole way — the frame never leaps ahead of the line.
export const HUD89_POST_CHOICE_VIEWPORT: PostChoiceViewportSettings = {
  yMaxByYear: {
    2029: 4,
    2030: 6,
    2031: 7,
    2032: 22,
    2033: 60,
    2035: 130,
    2038: 160,
    2041: 1000,
  },
  // Post-reveal, the timeline's right edge steps once a year to mid of the
  // year after next (current year + 1.5, fractional years supported): the
  // frame stays stable within a story year, and every step shows the line's
  // end plus ~a year and a half of dashed future — never the line running
  // into the right edge.
  futureWindowYears: 1.5,
}

// Tab 17 — the dist-of-power frontier fill treatment: frontier-lab cells
// solid red, their icons punched out in the page background color.
export const HUD17_FRONTIER_RED_FILL = {
  fillColor: "var(--accent)",
  iconColor: "#fffff8",
}

// Tab 24 — tab 17's canvas, scaled up a notch: the earth's canvas grows
// 400 → 440 (about its center), and the graph's solid red capability line
// thickens 4 → 5 (the dashed black future line stays 2.5).
export const HUD24_GLOBE_SIZE_PX = 440
export const HUD24_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX = 5

// Tab 18 — the below-the-HUD detail slot. Same providers and sticky zone as
// tab 15, but nothing on the row itself gets replaced: hovering a policy
// square, a stat-row metric, or a treemap cell renders that item's detail in
// the labor strip's spot BELOW the row (BelowHudDetailSwapTarget). The power
// breakdown uses the full HUD width there, so it lays out as THREE
// side-by-side region columns. The capability-line ladder takeover and the
// click-open power-duo panels behave exactly like tab 15.
// `ladderRowHighlightDisabled` drops the gray pill behind the takeover
// ladder's current row (the no-grays tab 10).
// `powerHoverRedOutline` (tab 1): hovering a distribution-of-power group
// outlines ITS cells in red with red icons; the other groups stay full black
// (instead of the stock treatment, which dims them).
// `powerHoverRedFill` (tab 2): the hovered group's cells fill solid red with
// white icons instead; the other groups stay full black.
// `powerBreakdownEmphasis` (tabs 3–5): how the below-HUD breakdown marks the
// hovered region's column — "chip" / "frame" / "rail" (see
// POWER_BREAKDOWN_EMPHASIS_STYLE in powerHoverSwap.tsx).
// `powerMetricLabel` (tabs 9–13): how the breakdown names its metric
// (H100-equivalent compute) — see POWER_METRIC_COPY in belowHudDetailSwap.tsx.
// `powerItemHighlightDisabled` (tab 17): no per-item highlights in the
// breakdown — the column-level emphasis line alone marks the hovered group.
// `capabilityLadderHighlight` (tabs 18–22): the red treatment replacing the
// takeover ladder's gray current-row pill — see
// CAPABILITY_LADDER_HIGHLIGHT_STYLE in capabilityHoverSwap.tsx.
// `policyDetailTreatment` (tabs 23–27): how the hovered policy's below-HUD
// detail renders — see the POLICY_DETAIL_*_STYLE consts in policyHoverSwap.tsx.
// `capabilityLadderDescriptionLayout` (tabs 28–29): the ladder description
// cells' layout fixing uneven / hover-shifting wraps ("compact" / "two-line")
// — see CAPABILITY_LADDER_DESCRIPTION_LAYOUT_STYLE in capabilityHoverSwap.tsx.
// `powerBreakdownHierarchy` (tab 1): the capability-takeover-style hierarchy
// for the breakdown — see POWER_BREAKDOWN_HIERARCHY_STYLE in powerHoverSwap.tsx.
// `metricHoverRedChip` (tab 3): the hovered stat-row metric (Employment etc.)
// inverts into a rounded red square with white text — see
// METRIC_TRIGGER_RED_CHIP_STYLE in metricHoverSwap.tsx.
// `policyMapDesign` (tabs 7–21): EVERY hover in the policy area (squares and
// status labels) renders the all-policies MAP below the HUD — the full list
// with the hovered item highlighted in place and its definition in a
// reserved spot — instead of the single-policy detail / by-section list.
// The designs (highlight vocabulary × definition placement × map layout)
// live in policyMapHoverDetail.tsx.
// `policySquareHighlight` (tabs 23–27): how the GRID square under the pointer
// (and the hovered status label) marks itself while its detail shows below —
// see the POLICY_SQUARE_* consts in policyAppearance.ts. Also replaces the
// stock opacity-dim square hover.
// `policyLabelHoverDisabled` (tabs 23–27): the status labels ("Current
// Policy" …) are not hover targets — no red label, no by-section takeover;
// only the squares hover.
export type BelowDetailHudOpts = {
    ladderRowHighlightDisabled?: boolean
    capabilityLadderHighlight?: CapabilityLadderHighlight
    capabilityLadderDescriptionLayout?: CapabilityLadderDescriptionLayout
    powerHoverRedOutline?: boolean
    powerHoverRedFill?: boolean
    // Tab 17: frontier-lab cells in the dist-of-power treemap fill solid red
    // with page-background icons, at the SAME border width as non-frontier
    // cells (dropping the stock frontier outline's extra 0.5px) — see
    // HUD17_FRONTIER_RED_FILL below.
    powerFrontierRedFill?: boolean
    // Tabs 18–22: the below-HUD power breakdown splits by FRONTIER ACCESS
    // instead of geography (title, subtitle, frontier companies, others) —
    // see PowerFrontierSplitDesign (powerHoverSwap.tsx) and the
    // POWER_FRONTIER_SPLIT_* dicts (belowHudDetailSwap.tsx).
    powerBreakdownFrontierSplit?: PowerFrontierSplitDesign
    // Tabs 25–26: hovering the dist-of-power treemap highlights ONE company
    // at a time — only the exact cell under the pointer takes the red hover
    // treatment, not its whole country group (hoverScope "cell" in
    // powerTreemapStyleFade.ts).
    powerHoverSingleCompany?: boolean
    // Tabs 34/35/37: keeps the frontier distinction legible inside the
    // hovered country's red fill — "hollow-others" leaves the non-frontier
    // cells outline-only, "cream-frontier-outline" punches the frontier
    // outlines through in the page background color. See
    // hoveredRegionFrontierDistinction (powerTreemapStyleFade.ts).
    powerHoverFrontierDistinction?: "hollow-others" | "cream-frontier-outline"
    // Tab 24: the globe's canvas size (PlaybookGlobe default 400; grows
    // around the earth's center) — see HUD24_GLOBE_SIZE_PX.
    globeSizePx?: number
    // Tab 24: the graph's solid red capability line stroke — unset keeps
    // HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX (4). The dashed black
    // future line is untouched either way.
    capabilityPastLineStrokeWidthPx?: number
    // Tab 49: the graph's capability line clips to the red dither field's
    // TOP edge — nothing of the line paints above it; the in-field portion
    // still shows (clipLinesToDitherTop, extrapolated-graph/types.ts).
    capabilityLineClipToDitherTop?: boolean
    powerBreakdownEmphasis?: PowerBreakdownRegionEmphasis
    powerMetricLabel?: PowerBreakdownMetricLabel
    powerItemHighlightDisabled?: boolean
    powerBreakdownHierarchy?: boolean
    policyDetailTreatment?: PolicyDetailTreatment
    metricHoverRedChip?: boolean
    policyMapDesign?: PolicyMapDesignId
    policySquareHighlight?: PolicySquareHighlight
    policyLabelHoverDisabled?: boolean
    // Tabs 1–5: hovering the labor section marks the SECTION with the
    // design's hover look (it stays put) and replaces the policies/
    // dist-of-power row above with the labor detail — see laborHoverSwap.tsx
    // for the looks and the (placeholder) copy.
    laborHoverDesign?: LaborHoverDesignId
    // Tab 16: the whole status ROW hovers as the label (gaps included), the
    // hovered label bolds + reds, and the map marks the hovered section with
    // the power breakdown's under-header rule (see policyHoverSwap's
    // statusRowHover + the "hud0-lean-status-underline" map design).
    policyStatusRowHover?: boolean
    // Hovering the GLOBE swaps the lower half to the ranked datacenter list
    // (name / region / size), with the datacenter under the pointer
    // highlighted in its row. The value picks the list's size visual — see
    // GlobeHoverListVariant in globeHoverSwap.tsx and the GLOBE_DC_* consts
    // in GlobeHoverDatacenterList.tsx. Unset keeps the feature entirely off.
    globeHoverList?: GlobeHoverListVariant
    // The datacenter list's row layout (one full column, capped two-column,
    // or the tightened no-cap two-column fit) — see GLOBE_DC_LIST_LAYOUTS in
    // GlobeHoverDatacenterList.tsx.
    globeHoverListLayout?: GlobeHoverListLayout
    // How the hovered datacenter's row marks itself in the list — see
    // GlobeHoverListRowHighlight in globeHoverSwap.tsx and
    // GLOBE_DC_ROW_HIGHLIGHT_STYLE in GlobeHoverDatacenterList.tsx. Unset =
    // the stock red-bold text.
    globeHoverListRowHighlight?: GlobeHoverListRowHighlight
    // How the EARTH itself responds while the globe hover is engaged — see
    // GlobeEarthHoverTreatment / GLOBE_EARTH_HOVER_STYLE in
    // globe/datacenterScreenPositions.ts. Unset = no earth response.
    globeHoverEarthTreatment?: GlobeEarthHoverTreatment
    // The look of the rule that separates a hover detail from the canvas
    // above it — drives BOTH the shared HudHoverDetailRule and the labor
    // section's "rule-above" rule. The treatments' literal knobs live in
    // HUD_HOVER_RULE_TREATMENTS (hudHoverDetailHeader.tsx). Unset = "stock"
    // (2px solid red). Any non-stock treatment ALSO switches the detail
    // geometry: details start where the covered content starts, the rule
    // hangs centered in the gap above, and the takeovers drop their rule.
    hoverRuleTreatment?: HudHoverRuleTreatmentId
    // The hover details' title/description type — faces and sizes per tier,
    // knobs in HUD_HOVER_HEADER_TREATMENTS (hudHoverDetailHeader.tsx).
    // Unset = "stock" (13px caps mono / 13px sans).
    hoverHeaderTreatment?: HudHoverHeaderTreatmentId
    // Drop the "Click for details" affordance from every hover detail
    // (HudHoverClickHint renders nothing).
    hoverClickHintDisabled?: boolean
    // Tabs 1–5: the red takeover-risk dither field ABOVE the capability line
    // becomes its own hover area — the field lightens to 60%
    // (TAKEOVER_RISK_AREA_HOVER_OPACITY, ExtrapolatedGraphRenderer) and the
    // HUD's lower half swaps to the "AI Takeover Risk" explainer
    // (TAKEOVER_RISK_HOVER_COPY, capabilityHoverSwap.tsx). The hover starts
    // 12px above the line (the point hover keeps the line) and the globe's
    // opaque pixels keep the datacenter hover (GlobeHoverPointerGate).
    takeoverRiskAreaHover?: boolean
    // Tabs 2–5: the visual the risk explainer appends below its subtitle —
    // the risk-level ladder or the yearly dot-density likelihood chart, each
    // with the live "85% in 2027" header phrase. See RISK_HOVER_DETAIL_STYLE
    // / RISK_LEVEL_LADDER_STYLE / RISK_DOT_CHART_STYLE in
    // capabilityHoverSwap.tsx. Unset = title + subtitle only.
    riskHoverDetail?: RiskHoverDetailVisual
    // Tabs 4–5: while the risk field is hovered, a dashed "what if AI foomed
    // right now" curve takes off from the capability line at the cursor's
    // year, topped by that year's takeover-risk likelihood — "percent" reads
    // "85% takeover risk", "level" reads "Very High Risk". Knobs and copy in
    // RiskFoomPreviewOverlay.tsx.
    riskFoomPreview?: RiskFoomPreviewMode
    // Tabs 6–10: the foom curve's visual treatment — subtle weights/textures
    // and the line-morph/branch takes (the morph designs clip the dashed
    // future capability line at the takeoff, so the foom reads as the line
    // itself fooming). See RISK_FOOM_DESIGNS in RiskFoomPreviewOverlay.tsx.
    // Unset = "stock" (tabs 4/5).
    riskFoomDesign?: RiskFoomDesignId
    // Tab 9: the Top-Expert-Dominating definition caption under the foom
    // readout in the canvas — see RISK_FOOM_TED_CAPTION
    // (RiskFoomPreviewOverlay.tsx).
    riskFoomTedCaption?: boolean
    // Tabs 6–10: how the risk explainer hints at what Top-Expert-Dominating
    // MEANS — "inline" weaves the ladder's one-liner into the subtitle,
    // "footnote" appends a small labeled line under it (copy in
    // TAKEOVER_RISK_TED_FOOTNOTE / TAKEOVER_RISK_HOVER_DESCRIPTION_TED_INLINE,
    // capabilityHoverSwap.tsx). Unset = no hint.
    riskHoverTedDefinition?: RiskHoverTedDefinition
    // Tab 14: the COMBINED hover — the whole graph is one hover zone (use
    // INSTEAD of takeoverRiskAreaHover): the point hover keeps its dot and
    // ladder everywhere, the foom curve takes off from the hover spot, the
    // red field lightens, and the takeover explainer carries both the R&D
    // speedup and the foom-risk stat (COMBINED_CAPABILITY_RISK_HOVER_COPY,
    // capabilityHoverSwap.tsx). Sets the graph's riskFoomOnPointHover and
    // the swap's combinedRiskDetail.
    riskCombinedHover?: boolean
    // Tab 19: which risk the combined hover's stat carries — "foom"
    // (default; the if-scaled-to-TED table, with the foom curve) or
    // "current-level" (the field's shading AT the line — the risk of the
    // level actually reached that year, no foom curve; the % climbs as the
    // cursor sweeps into higher-capability years). Copy in
    // COMBINED_CURRENT_LEVEL_RISK_COPY (capabilityHoverSwap.tsx).
    riskCombinedSemantics?: CombinedRiskSemantics
    // Tabs 22–24: the combined explainer's STACKED layout — the capability
    // hover's own header block (title, subtitle, speedup stat, ladder) with
    // an "AI Takeover Risk"-headed section under it carrying the field
    // hover's simple definition; riskCombinedRiskLine adds one live
    // percentage line there ("Risk level: 5%" / "Risk if
    // Top-Expert-Dominating AI level was achieved this year: 80%") — see
    // COMBINED_RISK_LINE_COPY / _STYLE (capabilityHoverSwap.tsx).
    riskCombinedLayout?: CombinedRiskLayout
    riskCombinedRiskLine?: CombinedRiskLine
    // Tab 20: the 2D risk-space explorer riding the risk-field hover — a
    // square marks the cursor's (year × capability-level) cell with the
    // level's name and the field's own risk shading at that exact point
    // ("this level, reached this year → this risk"), so the reader surveys
    // the WHOLE space, not just the foom-to-TED slice. Knobs and copy in
    // RISK_SPACE_EXPLORER_STYLE / RISK_SPACE_RISK_TEMPLATE
    // (RiskFoomPreviewOverlay.tsx). Pair with takeoverRiskAreaHover.
    // "timeline-guide" (tab 25) adds a dotted drop line from the square to
    // the timeline and the hovered year in red at the axis
    // (RISK_SPACE_TIMELINE_GUIDE_STYLE).
    riskSpaceExplorer?: RiskSpaceExplorerMode
    // Tab 26: while the risk field is hovered, the hovered year in red on
    // the timeline and a dotted line rising to the Top-Expert-Dominating
    // level, labeled when that height is in frame — the picture of the
    // ted-line panel's claim (RISK_TED_GUIDE_STYLE / RISK_TED_GUIDE_LABEL,
    // RiskFoomPreviewOverlay.tsx).
    // "below-earth" (tab 34) keeps the guide in the graph body, under the
    // globe, instead of the above-globe layer.
    riskTedGuide?: RiskTedGuideMode
    // Tab 32: the ted-line hover appends the FULL capability ladder (every
    // level with its one-liner), the Top-Expert-Dominating row pinned as
    // the highlighted one.
    riskHoverCapabilityLadder?: boolean
    // Tab 33: overrides the pre-choice close-up frame's y-axis max (stock 3)
    // — zoomed out so the Top-Expert-Dominating level sits just inside the
    // frame's top; see HUD33_PRE_CHOICE_Y_MAX.
    preChoiceYAxisMax?: number
    // Tab 33: renders the graph on the LOG y-scale — at the zoomed-out max a
    // linear axis flattens the early capability line into a ~2px hairline;
    // log keeps it a readable curve in the bottom ~16%.
    graphLogScale?: boolean
    // Tabs 35–39: the ted-line panel's skimmability layout — see
    // RiskTedPanelDesignId / RISK_TED_PANEL_COPY / _STYLE
    // (capabilityHoverSwap.tsx). Unset keeps tab 26's layout.
    riskTedPanelDesign?: RiskTedPanelDesignId
    // Tab 40: two keys at the stacked layout's bottom — Takeover Risk and
    // Delayed Actor Risk, one column each, with a small dither swatch
    // showing which field's dots they mean (RISK_KEYS_FOOTER_COPY / _STYLE,
    // capabilityHoverSwap.tsx).
    riskKeysFooter?: boolean
    // Tabs 41–45: how tab 40's panel separates its levels (rule between
    // sections / type-scale contrast / capability-line icon / footnote keys
    // / run-in keys) — see CombinedHierarchyId and
    // COMBINED_HIERARCHY_STYLE (capabilityHoverSwap.tsx).
    riskCombinedHierarchy?: CombinedHierarchyId
    // Tabs 46–50: the ALWAYS-ON risk readout on the graph (no hover) — the
    // current-level risk at the current story date, per design: "corner" /
    // "under-year-pill" / "field-label" / "current-dot" / "edge-meter". See
    // RiskAmbientOverlay + RISK_AMBIENT_STYLE / _COPY
    // (RiskFoomPreviewOverlay.tsx).
    riskAmbientDisplay?: RiskAmbientDesignId
    // Tab 53: the red field does NOT lighten while hovered — the hover keeps
    // its panel swap and cursor-year publishing, the dither stays
    // full-strength.
    riskHoverDimDisabled?: boolean
    // Tabs 55–59: how tab 19's combined explainer organizes its information
    // — see CombinedInfoLayoutId / COMBINED_INFO_LAYOUT_STYLE /
    // COMBINED_LABELED_DESCRIPTION (capabilityHoverSwap.tsx).
    riskCombinedInfoLayout?: CombinedInfoLayoutId
    // Tab 72: the risk value displays as the word scale (Very Low … Very
    // High) instead of the percentage — mapping in RISK_WORD_SCALE
    // (capabilityHoverSwap.tsx).
    riskCombinedRiskWord?: boolean
    // ── Tabs 66–70: the ZONE-CLARITY round (which hover zone am I in, and
    // make crossing the line/field boundary feel smooth) ─────────────────
    // Tabs 66–68, 70: crossing the boundary runs the lower-half panel
    // handoff on the hover-swap fade clock (old explainer out, new in on
    // the stagger) instead of hard-swapping in one frame — see
    // CapabilityHoverSwapTarget's contentKey (capabilityHoverSwap.tsx).
    riskZonePanelFade?: boolean
    // Tabs 66–70: the field lighten (and tab 67's line highlight) EASES
    // over RISK_ZONE_DIM_TWEEN_MS (ExtrapolatedGraphRenderer) instead of
    // snapping at the boundary.
    riskZoneDimTween?: boolean
    // Tab 67: the capability line thickens while the LINE zone owns the
    // hover (LINE_HOVER_HIGHLIGHT_EXTRA_STROKE_PX) — each zone's own ink
    // responds: line hover thickens the line, field hover lightens the
    // field.
    riskZoneLineHighlight?: boolean
    // Tab 68: ONE cursor guide column across both zones — black axis-to-line
    // in the line zone, stretching red to the Top-Expert-Dominating height
    // in the field, CSS-morphing between the two. Replaces the point dot/
    // guide, axis year text, and TED guide — see
    // RISK_ZONE_CONTINUOUS_GUIDE_STYLE (RiskFoomPreviewOverlay.tsx).
    riskZoneContinuousGuide?: boolean
    // Tab 69: BOTH zones show the same stacked capabilities + takeover-risk
    // panel; the hovered zone's section takes the red emphasis (marker
    // square + red title), which GLIDES across the boundary — see
    // ZONE_EMPHASIS_PANEL_STYLE (capabilityHoverSwap.tsx).
    riskZoneStackedPanel?: boolean
    // Tab 70: INVERTED dim — the field lightens while the LINE zone is
    // hovered and keeps full ink while it is itself hovered; the zone under
    // the pointer stays saturated.
    riskZoneDimInverted?: boolean
    // Picks tab 2 (Joanna 2026-07-15): the field's hover response is the
    // dither DOTS shrinking (RISK_ZONE_DOT_SHRINK_RADIUS_SCALE,
    // ExtrapolatedGraphRenderer) instead of the layer losing opacity —
    // full ink, visibly thinner field.
    riskZoneDimDotShrink?: boolean
    // Picks tab 6 (Joanna 2026-07-15): the earth's opacity while the risk
    // dither is hovered (0.5 = half strength), easing both ways — see
    // GLOBE_FIELD_HOVER_FADE_MS (HudPersistentGlobeAndGraph.tsx). Unset =
    // no fade.
    globeFieldHoverFade?: number
    // Tab 75: tab 69's stable-panel idea in tab 65's TWO-COLUMN grammar —
    // both zones show the same two metric columns (capabilities / takeover
    // risk, icon + live value + definition each) and the hovered zone's
    // column takes the focus via a gliding red top bar + red name — see
    // ZONE_COLUMNS_PANEL_STYLE (capabilityHoverSwap.tsx).
    riskZoneColumnsPanel?: boolean
    // Tab 77: the zone-columns panel appends tab 76's agent-level list
    // (names + gliding red mark + one active one-liner), the current level
    // tracking the cursor's year in either zone.
    riskZoneColumnsLevelList?: boolean
    // Tab 76's clarity variants: how the ladder-in-column panel names its
    // two sides — see LadderColumnClarityDesign / LADDER_COLUMN_CLARITY_*
    // (capabilityHoverSwap.tsx).
    riskLadderColumnClarity?: LadderColumnClarityDesign
    // Tab 87: the takeover detail starts flush with the covered row's top
    // edge — the panel headers align with where the dist-of-power
    // treemap's edge was.
    riskTakeoverFlushTop?: boolean
    // Tab 34 (2026-07-15 refinement): the red dither swatch inline with the
    // "AI Takeover Risk" title in the field hover's panel — the field's
    // icon next to its name; see RISK_HOVER_TITLE_SWATCH_GAP_PX
    // (capabilityHoverSwap.tsx).
    riskHoverTitleSwatch?: boolean
    // The matched-guides tab (34's sibling): the capability side's guide
    // line wears the TED guide's ORIGINAL treatment — 1px, 2-3 dash, full
    // strength, black — so both zones' cursor columns match at the red
    // one's old weight. See POINT_GUIDE_TED_DASH_*
    // (GraphPointHoverTooltip.tsx).
    pointGuideTedDash?: boolean
    // ── The 2026-07-15 restructure round (Joanna's four changes) ─────────
    // The graph hover replaces ONLY the policies/dist-of-power row — the
    // labor strip below stays live, set off by the hover-separator rule
    // hanging in the gap between them (CapabilityHoverLaborDivider,
    // capabilityHoverSwap.tsx). Pair with a SHORT panel layout
    // (riskCombinedInfoLayout: "level-cells-defs") — the row's footprint
    // has no room for the ladder.
    capabilityHoverRowOnly?: boolean
    // The labor hover's row detail becomes the AGENT LEVEL TABLE (the
    // capability ladder, story-date level marked) instead of the human/AI
    // labor definitions — see LaborHoverAgentLadderDetail /
    // LABOR_HOVER_AGENT_LADDER_COPY (laborHoverSwap.tsx).
    laborHoverAgentLadder?: boolean
    // The stat row carries R&D Speedup + Takeover Risk (the graph hover's
    // two live numbers, at the story date) in place of Safety Researchers +
    // Total Slowdown — see RdSpeedupStat / TakeoverRiskStat
    // (CapabilityRiskStats.tsx); hovering them shows their definitions in
    // the below-HUD slot like the other stats (METRIC_HOVER_DEFINITIONS
    // "rdSpeedup" / "takeoverRisk").
    statsRdSpeedupAndRisk?: boolean
}

// The hover-swap context providers for the below-detail HUD body. Shared by
// the desktop variant and the mobile expanded dashboard: the providers must
// enclose BOTH the graph (whose line hover feeds the capability ladder via
// CapabilityHoverBridge) and the panels (where the swap targets render).
export const BelowDetailHudSwapProviders = ({
  opts,
  children,
}: {
  opts?: BelowDetailHudOpts
  children: ReactNode
}) => (
  <HudHoverRuleTreatmentContext.Provider value={opts?.hoverRuleTreatment ?? "stock"}>
  <HudHoverHeaderTreatmentContext.Provider value={opts?.hoverHeaderTreatment ?? "stock"}>
  <HudHoverClickHintDisabledContext.Provider value={opts?.hoverClickHintDisabled ?? false}>
  <GlobeHoverSwap
    variant={opts?.globeHoverList}
    listLayout={opts?.globeHoverListLayout}
    rowHighlight={opts?.globeHoverListRowHighlight}
    earthTreatment={opts?.globeHoverEarthTreatment}
  >
  <CapabilityHoverSwap
    ladderRowHighlightDisabled={opts?.ladderRowHighlightDisabled}
    ladderHighlight={opts?.capabilityLadderHighlight}
    ladderDescriptionLayout={opts?.capabilityLadderDescriptionLayout}
    riskHoverDetail={opts?.riskHoverDetail}
    riskHoverTedDefinition={opts?.riskHoverTedDefinition}
    riskHoverCapabilityLadder={opts?.riskHoverCapabilityLadder}
    riskTedPanelDesign={opts?.riskTedPanelDesign}
    riskKeysFooter={opts?.riskKeysFooter}
    combinedHierarchyDesign={opts?.riskCombinedHierarchy}
    combinedInfoLayout={opts?.riskCombinedInfoLayout}
    combinedRiskWord={opts?.riskCombinedRiskWord}
    zonePanelFade={opts?.riskZonePanelFade}
    zoneStackedEmphasis={opts?.riskZoneStackedPanel}
    zoneColumnsEmphasis={opts?.riskZoneColumnsPanel}
    zoneColumnsLevelList={opts?.riskZoneColumnsLevelList}
    ladderColumnClarityDesign={opts?.riskLadderColumnClarity}
    takeoverDetailFlushTop={opts?.riskTakeoverFlushTop}
    riskHoverTitleSwatch={opts?.riskHoverTitleSwatch}
    combinedRiskDetail={
      opts?.riskCombinedHover ? opts?.riskCombinedSemantics ?? "foom" : undefined
    }
    combinedRiskLayout={opts?.riskCombinedLayout}
    combinedRiskLine={opts?.riskCombinedRiskLine}
  >
    <PowerHoverSwap
      breakdownRegionEmphasis={opts?.powerBreakdownEmphasis}
      breakdownMetricLabel={opts?.powerMetricLabel}
      breakdownItemHighlightDisabled={opts?.powerItemHighlightDisabled}
      breakdownHierarchy={opts?.powerBreakdownHierarchy}
      breakdownFrontierSplit={opts?.powerBreakdownFrontierSplit}
    >
      {children}
    </PowerHoverSwap>
  </CapabilityHoverSwap>
  </GlobeHoverSwap>
  </HudHoverClickHintDisabledContext.Provider>
  </HudHoverHeaderTreatmentContext.Provider>
  </HudHoverRuleTreatmentContext.Provider>
)

const makeBelowDetailHudBody = (opts?: BelowDetailHudOpts) => () => (
  <BelowDetailHudSwapProviders opts={opts}>
    <HudPersistentGlobeAndGraph
      currentYearPill
      showAICapabilitiesLabel={false}
      capabilityPastLineColor={HUD87_CAPABILITY_PAST_LINE_COLOR}
      capabilityPastLineStrokeWidth={
        opts?.capabilityPastLineStrokeWidthPx ?? HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX
      }
      clipLinesToDitherTop={opts?.capabilityLineClipToDitherTop}
      capabilityLineFloorValue={HUD87_CAPABILITY_LINE_FLOOR_VALUE}
      globeSize={opts?.globeSizePx}
      takeoverRiskAreaHover={opts?.takeoverRiskAreaHover}
      riskFoomPreview={opts?.riskFoomPreview}
      riskFoomDesign={opts?.riskFoomDesign}
      riskFoomTedCaption={opts?.riskFoomTedCaption}
      riskFoomOnPointHover={
        // On for BOTH combined semantics — it publishes the cursor's year
        // (the current-level stat needs it for the table lookup) and dims
        // the field; the foom curve itself only draws when riskFoomPreview
        // is also set (tab 19 doesn't set it).
        opts?.riskCombinedHover
      }
      riskSpaceExplorer={opts?.riskSpaceExplorer}
      riskTedGuide={opts?.riskTedGuide}
      riskAmbientDisplay={opts?.riskAmbientDisplay}
      riskHoverDimDisabled={opts?.riskHoverDimDisabled}
      riskZoneDimTween={opts?.riskZoneDimTween}
      riskZoneDimInverted={opts?.riskZoneDimInverted}
      riskZoneDimDotShrink={opts?.riskZoneDimDotShrink}
      globeFieldHoverFade={opts?.globeFieldHoverFade}
      riskZoneContinuousGuide={opts?.riskZoneContinuousGuide}
      pointGuideTedDash={opts?.pointGuideTedDash}
      highlightLineWhilePointHovered={opts?.riskZoneLineHighlight}
      preChoiceYAxisMax={opts?.preChoiceYAxisMax}
      graphLogScale={opts?.graphLogScale}
      showCurrentPointMarker
      currentPointMarkerRadius={HUD87_CURRENT_POINT_DOT_RADIUS_PX}
      showCurrentPointCapabilityLabel
      minimumGraphDate={HUD87_GRAPH_MINIMUM_DATE}
      // The line hover already explains the graph via the ladder takeover;
      // the whole-graph "AI Capabilities / Takeover Risk" card on top of it
      // was redundant.
      hideGraphExplainerTooltip
    >
      <CapabilityHoverBridge />
      {/* Globe hover driver (inert unless globeHoverList is set): gates the
          pointer per-pixel so the globe hover engages exactly on the globe's
          opaque pixels (the sphere + protruding datacenter stacks) and the
          graph keeps its hover everywhere else. */}
      <GlobeHoverPointerGate />
    </HudPersistentGlobeAndGraph>
    <BelowDetailHudPanels opts={opts} />
    {/* Narration bar: normal flow directly below the labor distribution
        strip (the panel stack's last element), at the HUD's full width.
        Desktop-only — the mobile expanded dashboard renders
        BelowDetailHudPanels directly and doesn't get it. */}
    <GraphAudioControls />
  </BelowDetailHudSwapProviders>
)

// Where the capability takeover paints, per capabilityHoverRowOnly: stock
// tabs wrap the WHOLE lower half (policies row + labor strip) in the swap
// target; the row-only restructure wraps just the policies/dist-of-power
// row, so the labor strip stays live under the hover. Rendered at both
// spots below with complementary `enabled` — the disabled spot is a
// pass-through fragment, so stock tabs' DOM is unchanged.
const MaybeCapabilityHoverSwapTarget = ({
  enabled,
  children,
}: {
  enabled: boolean
  children: ReactNode
}) => (enabled ? <CapabilityHoverSwapTarget>{children}</CapabilityHoverSwapTarget> : <>{children}</>)

// The below-detail HUD's panel stack (policies, stats, power treemap, labor
// strip) with the full hover-swap/detail wiring. Shared by the desktop
// variant (default widths) and the mobile expanded dashboard (narrower power
// column + compact stats). Must render inside BelowDetailHudSwapProviders.
export const BelowDetailHudPanels = ({
  opts,
  powerColumnWidthPx = 220,
  statsCompact = false,
  galaxyHideForceVisible = false,
  // Desktop's wide column fits both labor-legend entries on ONE line
  // ("inline"). The mobile dashboard passes "rows" — since main's
  // "at up to ×N speed" copy, the single line overflows phone columns
  // (clipped speed), while stacked rows leader-align each speed to the
  // column's right edge.
  laborLegendLayout = "inline",
}: {
  opts?: BelowDetailHudOpts
  powerColumnWidthPx?: number
  statsCompact?: boolean
  galaxyHideForceVisible?: boolean
  laborLegendLayout?: "inline" | "rows"
}) => {
  const statLabelFontSize = statsCompact ? "7.5px" : "8.5px"
  return (
    <GalaxyHide forceVisible={galaxyHideForceVisible}>
      {/* overflowVisibleWhenSettled: see the tab-0 body — keeps the full
          ladder takeover visible past the lower HUD's footprint. */}
      <AnimateYear
        targetDate={new Date("2027-06-01")}
        forceVisible
        overflowVisibleWhenSettled
      >
        <GlobeHoverSwapTarget>
        <MaybeCapabilityHoverSwapTarget enabled={!opts?.capabilityHoverRowOnly}>
        <MetricHoverSwap
          triggerHighlight={opts?.metricHoverRedChip ? "red-chip" : undefined}
        >
          <PolicyHoverSwap
            detailTreatment={opts?.policyDetailTreatment}
            mapDesign={opts?.policyMapDesign}
            squareHighlight={opts?.policySquareHighlight}
            labelHoverDisabled={opts?.policyLabelHoverDisabled}
            statusRowHover={opts?.policyStatusRowHover}
          >
          <LaborHoverSwap
            design={opts?.laborHoverDesign}
            agentLadder={opts?.laborHoverAgentLadder}
          >
            {/* Opening a metric detail clears EVERY hover swap first, so
                nothing red-from-hover (metric chip, treemap fill, policy
                square) is still red while the open animation plays. */}
            <ClearHudHoversWhileDetailOpen />
            <MaybeCapabilityHoverSwapTarget enabled={Boolean(opts?.capabilityHoverRowOnly)}>
            <LaborHoverRowTarget>
            {/* items-stretch + the left column as a flex column: the treemap
                (height 120) sets the row's height, and the stats row rides
                the column's BOTTOM edge (mt-auto below) so the stat labels
                bottom-align with the treemap's bottom edge. */}
            <HoverSwapStickyZone className="flex justify-between gap-[20px] items-stretch mt-[2px]">
              <div className="flex-1 min-w-0 flex flex-col">
                <PoliciesPanel
                  layout="rows"
                  reverse={false}
                  statusAppearance={HUD87_POLICY_STATUS_APPEARANCE}
                  strokeWidth={1.5}
                  borderRadius={HUD69_BOX_RADIUS_PX}
                  squareSize={16}
                  // 1px (not 2) so the story-start state — 8 off-the-table
                  // boxes after Universal Basic Galaxies unlocks only at
                  // Plan A — leaves room for the full "Off the Table" label.
                  gap={1}
                  crowdedStatusLabels={{
                    current: "Policy",
                    in_discussion: "Discussion",
                    off_the_table: "Off",
                  }}
                  labelUppercase={false}
                  labelFontSize="8.5px"
                  labelHug
                  labelHugAlign="end"
                  labelHugGapPx={5}
                  overlapLabel
                />
                {/* items-stretch under the red-chip treatment: every metric
                    trigger spans the row's full height, so the four hover
                    chips share one height instead of hugging their own
                    label's line count.
                    mt-auto pins the row to the column's bottom (= the
                    treemap's bottom edge); the negative bottom margin
                    cancels the red chips' vertical padding so the LABEL
                    TEXT's bottom (not the invisible chip plate's) sits on
                    that edge — the hover chips move up with the row.
                    Where text-box is supported the labels are trimmed to end
                    exactly at their last line's baseline
                    (METRIC_LABEL_BASELINE_TRIM_CLASS in the indicators), so
                    cancelling the chip padding alone puts the baseline on
                    the treemap's bottom edge — exact on every font/platform
                    and at both stat font sizes. The un-trimmed fallback
                    keeps Joanna's eyeballed extra -4 (2026-07-08), which
                    sinks the line box until the baseline sits about on the
                    edge for typical font metrics. The supports- condition
                    must match the trim class's declaration.
                    The +4 on both values lifts the baseline 4px above the
                    edge — deliberate air under the labels for visual
                    balance (2026-07-09). */}
                <div
                  className={`flex justify-between mt-auto font-mono ${opts?.metricHoverRedChip ? "items-stretch mb-[var(--stats-mb-untrimmed)] supports-[text-box:trim-end_cap_alphabetic]:mb-[var(--stats-mb-trimmed)]" : "items-start"} ${statsCompact ? "gap-x-1 [&_.font-bold]:text-[10px]" : "gap-x-3"}`}
                  style={opts?.metricHoverRedChip ? {
                    "--stats-mb-untrimmed": `${-METRIC_TRIGGER_RED_CHIP_STYLE.paddingYPx - 4 + 4}px`,
                    "--stats-mb-trimmed": `${-METRIC_TRIGGER_RED_CHIP_STYLE.paddingYPx + 4}px`,
                  } as CSSProperties : undefined}
                >
                  <EmploymentRate valueFirst label="Employment" labelFontSize={statLabelFontSize} />
                  <MedianIncome valueFirst label="Median Income" labelFontSize={statLabelFontSize} />
                  {opts?.statsRdSpeedupAndRisk ? (
                    // The restructure round: the graph hover's two live
                    // numbers in the data bar, where Safety Researchers /
                    // Total Slowdown normally sit.
                    <>
                      <RdSpeedupStat labelFontSize={statLabelFontSize} />
                      <TakeoverRiskStat labelFontSize={statLabelFontSize} />
                    </>
                  ) : (
                    <>
                      <AlignmentResearcherCount valueFirst label="Safety Researchers" units="" labelFontSize={statLabelFontSize} />
                      <SafetyTaxIndicator valueFirst label="Total Slowdown" labelFontSize={statLabelFontSize} />
                    </>
                  )}
                </div>
              </div>
              <ClaimPowerHoverOnEnter
                className="relative"
                style={{ width: powerColumnWidthPx }}
              >
                <DistributionOfPowerBubbles4Fade
                  height={120}
                  strokeColor="#000"
                  strokeWidth={1.5}
                  iconColor="#000"
                  backgroundColor="transparent"
                  maxWidth={powerColumnWidthPx}
                  gapPx={2}
                  borderRadiusPx={HUD70_POWER_RADIUS_PX}
                  hoveredRegionStrokeColor={opts?.powerHoverRedOutline || opts?.powerHoverRedFill ? "var(--accent)" : undefined}
                  hoveredRegionIconColor={opts?.powerHoverRedFill ? "#fff" : opts?.powerHoverRedOutline ? "var(--accent)" : undefined}
                  hoveredRegionFillColor={opts?.powerHoverRedFill ? "var(--accent)" : undefined}
                  hoverSingleCompany={opts?.powerHoverSingleCompany}
                  hoveredRegionFrontierDistinction={opts?.powerHoverFrontierDistinction}
                  frontierFillColor={opts?.powerFrontierRedFill ? HUD17_FRONTIER_RED_FILL.fillColor : undefined}
                  frontierIconColor={opts?.powerFrontierRedFill ? HUD17_FRONTIER_RED_FILL.iconColor : undefined}
                />
              </ClaimPowerHoverOnEnter>
            </HoverSwapStickyZone>
            </LaborHoverRowTarget>
            </MaybeCapabilityHoverSwapTarget>
            <BelowHudDetailSwapTarget className="mt-6">
              {opts?.capabilityHoverRowOnly ? (
                // Row-only takeover: the strip stays live under the graph
                // hover, set off by the hover-separator rule hanging in the
                // gap above it while the takeover shows.
                <div className="relative">
                  <CapabilityHoverLaborDivider />
                  <LaborHoverSectionHighlight>
                    <LaborDotStripLeaderRows legendLayout={laborLegendLayout} responsiveMaxHeight />
                  </LaborHoverSectionHighlight>
                </div>
              ) : (
                <LaborHoverSectionHighlight>
                  <LaborDotStripLeaderRows legendLayout={laborLegendLayout} responsiveMaxHeight />
                </LaborHoverSectionHighlight>
              )}
            </BelowHudDetailSwapTarget>
          </LaborHoverSwap>
          </PolicyHoverSwap>
        </MetricHoverSwap>
        </MaybeCapabilityHoverSwapTarget>
        </GlobeHoverSwapTarget>
      </AnimateYear>
    </GalaxyHide>
  )
}

// The production tab-0 body opts — ALSO used by the mobile expanded dashboard
// (plan-a/MobileHUD.tsx), which renders the same BelowDetailHudPanels inside
// the same BelowDetailHudSwapProviders so desktop and mobile stay in sync.
export const BELOW_DETAIL_HUD_BODY_OPTS: BelowDetailHudOpts = {
  powerHoverRedFill: true,
  powerBreakdownEmphasis: "underline",
  powerMetricLabel: "measured-below",
  policyDetailTreatment: "more-text-status-top",
  powerBreakdownHierarchy: true,
  metricHoverRedChip: true,
  policyMapDesign: "hud0-lean-status-underline",
  policyStatusRowHover: true,
  policySquareHighlight: "red-fill-invert",
  laborHoverDesign: "rule-above",
  // The globe hover (PR #115): ranked datacenter list, bars size visual,
  // one full column. Shared opts, so the mobile dashboard gets it too.
  globeHoverList: "bars",
  globeHoverListLayout: "one-column-all",
  // Joanna's 2026-07-09 picks from the globe-dc-hover-list exploration's
  // tab 18:
  // - hovered-row highlight "rail-cut": red left rail + red bold text; the
  //   old row cuts out at once (45ms linear), the new one settles in with a
  //   strong ease-out (120ms cubic-bezier(0.16,1,0.3,1)) — knobs in
  //   GLOBE_DC_RAIL_ANIM_VARIANTS (GlobeHoverDatacenterList.tsx);
  // - earth hover "rim-accent-thicker": the globe rim recolors to the
  //   accent red and grows 1px while the list is up
  //   (GLOBE_EARTH_HOVER_STYLE in globe/datacenterScreenPositions.ts).
  globeHoverListRowHighlight: "rail-cut",
  globeHoverEarthTreatment: "rim-accent-thicker",
  // Joanna's 2026-07-11 picks from the hover-separator/header exploration
  // (july11a tab 6):
  // - the hover-detail separator: 1px dotted black, hung centered in the gap
  //   above the detail; details start where the covered content started
  //   (+small nudges); the capability and datacenter takeovers get NO rule —
  //   knobs in HUD_HOVER_RULE_TREATMENTS / HUD_HOVER_RULE_CENTERED_TOP_PX /
  //   TAKEOVER_DETAIL_TOP_NUDGE_PX (hudHoverDetailHeader.tsx) and
  //   BELOW_HUD_DETAIL_TOP_NUDGE_PX (belowHudDetailSwap.tsx);
  // - the hover-detail header type: both tiers at 11px ("smaller-type" in
  //   HUD_HOVER_HEADER_TREATMENTS, hudHoverDetailHeader.tsx);
  // - no "Click for details" affordance on the hover details.
  hoverRuleTreatment: "black-dotted",
  hoverHeaderTreatment: "smaller-type",
  hoverClickHintDisabled: true,
}

// Distilled single-variant branch: tab 53 of the policy-map-red-invert
// exploration — the below-the-HUD detail design with:
// - the ALIGNED metric details panel ("power-stack-links-outline-clean"):
//   temporal section titles, definition under the NOW title, bare stat
//   numbers, hover-matched type, grouped plans, chip header links
//   (outline-clean), chip-chevron back button;
// - the all-policies MAP as the policy hover, in the tab-16 status-row
//   treatment adopted from policy-map-red-invert (2026-07-07): the whole
//   status ROW hovers as one target ("hud0-lean-status-underline" +
//   policyStatusRowHover — hovered label bolds + reds, map marks the section
//   with the under-header rule), red-fill-invert square highlighting,
//   red-chip metric hovers;
// - the labor-section hover: section stays put with a red rule above it
//   ("rule-above"), the labor detail replacing the policies/power row;
// - the red-FILL treemap hover with the capability-hierarchy breakdown and
//   the short "Measured in …" line under the breakdown title
//   ("measured-below", 2026-07-07 — was the right-aligned inline definition);
// - the SEQUENTIAL-FADE detail open/close (picked from the tab 1–5 animation
//   exploration, 2026-07-07): HUD fully out, then panel in — no crossfade, no
//   scaled flights. The fade classes live on the layer divs in Playbook.tsx;
//   hover red is cleared at open (ClearHudHoversWhileDetailOpen below) and
//   CSS hover reds are neutralized on the closing layer by the hover-lock
//   variants in those same classes.
// The single production variant, rendered at ?hud=0. This branch adds — on
// top of the base opts above — the tab-18 datacenter-hover picks (rail-cut
// row highlight, accent-rim earth hover).
// Tab 33 — the pre-choice close-up's y-axis max, zoomed out so the
// Top-Expert-Dominating level (model-scale threshold 419) sits just inside
// the frame's top on the LOG axis (~95% height — enough headroom for the
// guide's label) while the early capability line still reads as a curve in
// the bottom ~16%. Pairs with graphLogScale: a linear axis at this max
// flattens the line into a hairline.
export const HUD33_PRE_CHOICE_Y_MAX = 560

// The production tab-0 configuration — the 2026-07-12/13 frontier-split
// pick (exploration tab 49): on top of BELOW_DETAIL_HUD_BODY_OPTS,
// - the capability ladder marked by the GLIDING RED LEVEL-METER SEGMENT
//   alone — no black rail line, bar flush with where the line's left edge
//   would be and sticking out right ("rail-glide-thick-right-no-line",
//   capabilityHoverSwap.tsx);
// - the below-HUD power breakdown split by FRONTIER ACCESS as country
//   columns with icons, underlined key, swapped subtitle
//   ("country-columns-icons-underline-key-subtitle-swapped",
//   powerHoverSwap.tsx / belowHudDetailSwap.tsx);
// - the graph's capability line clipped to the red dither field's top
//   edge — nothing of the line/dots/hover paints above the field
//   (capabilityLineClipToDitherTop; the field is top-anchored, so the
//   visible edge is the canvas top — see ExtrapolatedGraphRenderer).
// Also the base every exploration tab below rides on.
export const HUD0_BASE_OPTS: BelowDetailHudOpts = {
  ...BELOW_DETAIL_HUD_BODY_OPTS,
  capabilityLadderHighlight: "rail-glide-thick-right-no-line",
  powerBreakdownFrontierSplit: "country-columns-icons-underline-key-subtitle-swapped",
  capabilityLineClipToDitherTop: true,
}

export const HUD_VARIANTS: Array<() => ReactNode> = [
  // The capability-risk COMBINED HOVER (distilled from the risk-hover-picks
  // exploration's tab 1 / july13b tab 53, 2026-07-15) — the single variant,
  // rendered at ?hud=0. On top of the production base (HUD0_BASE_OPTS):
  // hovering anywhere on the graph swaps the HUD's lower half to a stacked
  // two-section explainer — the capabilities block (title with the
  // title-height capability-line icon, subtitle, live R&D-speedup stat, the
  // agent-level ladder) over the HUD's dotted hover rule and the two dither
  // keys as flowing colon lines ("TAKEOVER RISK: …" / "DELAYED ACTOR RISK:
  // …"). The red field does NOT lighten while hovered.
  makeBelowDetailHudBody({
    ...HUD0_BASE_OPTS,
    riskCombinedHover: true,
    riskCombinedSemantics: "current-level",
    riskCombinedLayout: "two-sections",
    riskKeysFooter: true,
    riskCombinedHierarchy: "title-icon-rule-run-in",
    riskHoverDimDisabled: true,
  }),
]


// Which capability-ladder treatment each tab's DETAILS PAGE uses — the
// details panel matches its tab's takeover ladder (Joanna 2026-07-12).
// Keyed by ?hud index; tabs not listed use the default. Consumed via
// DetailsLadderHighlightContext (metricPanelRedesigns.tsx), provided in
// Playbook.tsx.
export const HUD_DETAILS_LADDER_HIGHLIGHT_DEFAULT: CapabilityLadderHighlight =
  "rail-glide-thick"
export const HUD_DETAILS_LADDER_HIGHLIGHTS: Partial<Record<number, CapabilityLadderHighlight>> = {
  // The single variant rides the production base, so its details page
  // matches it: the red level-meter segment alone, no black rail line.
  0: "rail-glide-thick-right-no-line",
}

// Which metric-details panel design each tab uses (index-aligned with
// HUD_VARIANTS).
export const HUD_METRIC_PANEL_DESIGNS: Array<MetricPanelDesignId | null> = [
  // The production deep-simplification details panel.
  "minimal",
]

// Which mobile bottom-HUD design each tab uses (index-aligned with
// HUD_VARIANTS). "bubble" = the classic line-chart bubble; the
// "strip-*" ids are the compact-strip variants (76px production strip) —
// their per-variant knobs live in COMPACT_STRIP_VARIANTS
// (plan-a/mobile-strip/stripVariants.ts).
export type MobileHudDesignId = "bubble" | MobileStripDesignId
export const MOBILE_HUD_DESIGNS: MobileHudDesignId[] = [
  // The production compact micro-dither strip.
  "strip-micro-dither",
]

// Which Choose-a-Path diagram design each tab uses (index-aligned with
// HUD_VARIANTS; entries past the end fall back to 'stock' in Playbook.tsx).
// The design knobs live in branch-node/choosePathDesigns.tsx.
export const CHOOSE_PATH_DIAGRAM_DESIGNS: ChoosePathDesignId[] = [
  // The production 2029 Choose-a-Path redesign.
  "plan-a-full-row2",
]

// Each variant is a function returning the full HUD body. Variants that want
// the persistent globe + extrapolated graph include <HudPersistentGlobeAndGraph />
// directly; the text-only variant simply omits it.
//
// Cmd+ArrowLeft / Cmd+ArrowRight cycles in Playbook.tsx; ?hud=<index>
// deep-links by position.
//
// Rule (see hud/agents.md): no hooks live in this file. Each entry is just
// inline JSX that composes child components which read useHudContext()
// internally.
